import leafImg from "@/assets/sto-leaf-signature.png";

interface LeafSignatureProps {
  /**
   * - `watermark` (default): large, very low-opacity leaf anchored in a corner as
   *   ambient decoration inside a relatively-positioned section.
   * - `divider`: small centered leaf with hairline gold rules on either side,
   *   used to break editorial sections.
   * - `inline`: a single small leaf with no rules, for use beside headings.
   */
  variant?: "watermark" | "divider" | "inline";
  position?:
    | "top-left"
    | "top-right"
    | "bottom-left"
    | "bottom-right"
    | "center-right"
    | "center-left";
  size?: "sm" | "md" | "lg" | "xl";
  tone?: "light" | "dark";
  className?: string;
}

const sizeMap = {
  sm: "w-8 h-8 md:w-10 md:h-10",
  md: "w-14 h-14 md:w-20 md:h-20",
  lg: "w-24 h-24 md:w-64 md:h-64",
  xl: "w-32 h-32 md:w-[28rem] md:h-[28rem]",
};

// Mobile tucks watermarks into corners. Left/top offsets may bleed off-canvas,
// but right-side ones sit 1rem in (leaves are rotated, so their corners reach past the box): a negative right offset widens the
// page and causes horizontal scrolling on phones.
const positionMap = {
  "top-left": "-top-4 -left-6 md:top-10 md:left-10",
  "top-right": "-top-4 right-4 md:top-10 md:right-10",
  "bottom-left": "-bottom-4 -left-6 md:bottom-12 md:left-10",
  "bottom-right": "-bottom-4 right-4 md:bottom-12 md:right-10",
  "center-right": "top-1/2 -translate-y-1/2 right-4 md:right-8",
  "center-left": "top-1/2 -translate-y-1/2 -left-4 md:left-8",
};

const LeafSignature = ({
  variant = "watermark",
  position = "top-right",
  size = "lg",
  tone = "light",
  className = "",
}: LeafSignatureProps) => {
  if (variant === "divider") {
    const ruleColor =
      tone === "dark" ? "bg-primary-foreground/25" : "bg-accent/40";
    return (
      <div
        className={`flex items-center justify-center gap-5 my-12 md:my-16 ${className}`}
        aria-hidden
      >
        <span className={`h-px w-16 md:w-28 ${ruleColor}`} />
        <img
          src={leafImg}
          alt=""
          className={`${sizeMap.md} object-contain opacity-80`}
          loading="lazy"
          width={240}
          height={240}
        />
        <span className={`h-px w-16 md:w-28 ${ruleColor}`} />
      </div>
    );
  }

  if (variant === "inline") {
    return (
      <img
        src={leafImg}
        alt=""
        aria-hidden
        className={`${sizeMap[size]} object-contain opacity-90 ${className}`}
        loading="lazy"
        width={240}
        height={240}
      />
    );
  }

  // watermark
  const opacity =
    tone === "dark"
      ? "opacity-[0.05] md:opacity-[0.08]"
      : "opacity-[0.04] md:opacity-[0.07]";
  return (
    <img
      src={leafImg}
      alt=""
      aria-hidden
      className={`pointer-events-none select-none absolute ${positionMap[position]} ${sizeMap[size]} object-contain ${opacity} rotate-[8deg] ${className}`}
      loading="lazy"
      width={512}
      height={512}
    />
  );
};

export default LeafSignature;
